import { useState, useMemo, useEffect } from 'react';

import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { GET_CONTENT_POINTS_BY_OUTCOME } from './content-points.graphql';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { BoxPanel } from 'approot/shared/styles/box-panel.style';
import Text from 'approot/shared/styles/typography/text';
import { ContentPointBody } from './content-point-body';
import ContentPointsSearch from './content-points-search';
import { ContentPoint } from './content-points.constants';
import type {
  GetContentPointsByOutcomeQuery,
  GetContentPointsByOutcomeQueryVariables,
} from './content-points.graphql.generated';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';

type Props = {
  outcomeId: number | null;
  selectedContentPoints: ContentPoint[];
  onUpdate: (contentPoints: ContentPoint[], outcomeId: number | null) => void;
};

const ContentPointSearchByOutcome = ({
  outcomeId,
  selectedContentPoints,
  onUpdate,
}: Props) => {
  const [localSelectedContentPoints, setLocalSelectedContentPoints] = useState<
    ContentPoint[]
  >(selectedContentPoints);

  const { data: contentPointsData, loading: contentPointsLoading } =
    useLoggingQuery<
      GetContentPointsByOutcomeQuery,
      GetContentPointsByOutcomeQueryVariables
    >(GET_CONTENT_POINTS_BY_OUTCOME, {
      fetchPolicy: 'network-only',
      variables: {
        input: {
          outcomeId: outcomeId!,
        },
      },
      skip: !outcomeId,
    });

  const contentPoints = useMemo(() => {
    return contentPointsData?.getContentPointsByOutcome || [];
  }, [contentPointsData]);

  const selectedContentPointIds = useMemo(() => {
    return localSelectedContentPoints.map(cp => cp.id);
  }, [localSelectedContentPoints]);

  const handleContentPointSelected = (
    contentPoint: ContentPoint | undefined
  ) => {
    if (outcomeId && contentPoint) {
      const newContentPoints = [...localSelectedContentPoints, contentPoint];
      setLocalSelectedContentPoints(prev => newContentPoints);
      onUpdate(newContentPoints, outcomeId);
    }
  };

  const handleRemoveContentPoint = (contentPointId: number) => {
    const newContentPoints = localSelectedContentPoints.filter(
      cp => cp.id !== contentPointId
    );
    setLocalSelectedContentPoints(newContentPoints);
    onUpdate(newContentPoints, outcomeId);
  };

  const hasContentPoints = contentPoints.length > 0;

  useEffect(() => {
    setLocalSelectedContentPoints(selectedContentPoints);
  }, [selectedContentPoints]);

  return (
    <SettingsGroup $marginBottom={0}>
      <Box>
        <SettingsLabel htmlFor="content-points-search">
          Content Points
        </SettingsLabel>
        <ContentPointsSearch
          disabled={!outcomeId || contentPointsLoading || !hasContentPoints}
          contentPoints={contentPoints}
          selectedContentPointIds={selectedContentPointIds}
          onContentPointSelected={handleContentPointSelected}
        />
      </Box>

      {selectedContentPoints.length > 0 && (
        <Box $gap={12} $flexDirection="column" $marginTop={16}>
          {selectedContentPoints.map(contentPoint => (
            <BoxPanel
              key={contentPoint.id}
              onClick={() => handleRemoveContentPoint(contentPoint.id)}
              $isOverflowVisible
            >
              <Text $marginBottom={12}>
                <ContentPointBody content={contentPoint.content} />
              </Text>
              <Box $justifyContent="flex-end">
                <SecondaryButton
                  type="button"
                  $isSmall
                  aria-label={`Clear content point: ${contentPoint.content}`}
                >
                  Clear
                </SecondaryButton>
              </Box>
            </BoxPanel>
          ))}
        </Box>
      )}
    </SettingsGroup>
  );
};

export default ContentPointSearchByOutcome;
